import { useCallback, useEffect, useRef, useState } from "react";
import { getUserPicker } from "../../services/user.service";

const PAGE_SIZE = 20;

/**
 * Cursor-paginated, debounced member search.
 *
 * Extracted so the single-select AssigneePicker and the multi-select
 * AssigneeSelect share one implementation. Before this there were four
 * near-identical copies of this fetch across the app, three of which also
 * carried the same module-level-singleton pagination bug.
 *
 * `roles` is optional — omit it to search every member (the filter drawer
 * deliberately does), pass "HR,PROJECT MANAGER" to restrict.
 */
export default function useUserSearch({ roles, enabled = true } = {}) {
  const [users, setUsers] = useState([]);
  const [loadingInit, setLoadingInit] = useState(false);
  const [loadingMore, setLoadingMore] = useState(false);
  const [hasMore, setHasMore] = useState(false);

  const cursorRef = useRef(null);
  const searchRef = useRef("");
  const debounceRef = useRef(null);
  const inFlight = useRef(false);
  const latestRequest = useRef(0);

  const fetchUsers = useCallback(
    async (search, cursor) => {
      const paginating = Boolean(cursor);
      // A slow response for an older search term must not overwrite a newer one.
      const requestId = ++latestRequest.current;
      if (paginating) setLoadingMore(true);
      else setLoadingInit(true);
      try {
        const res = await getUserPicker({
          search,
          cursor,
          limit: PAGE_SIZE,
          ...(roles ? { roles } : {}),
        });
        if (requestId !== latestRequest.current) return;
        if (res?.error === false) {
          const items = (res.data || []).map((u) => ({
            _id: String(u._id),
            label: u.name || u.userName || "Unknown",
            // getUserPicker returns `file` as a bare URL string; task docs carry
            // { location }. Accept both.
            file: typeof u.file === "string" ? u.file : u.file?.location || null,
          }));
          setUsers((prev) => (paginating ? [...prev, ...items] : items));
          cursorRef.current = res.nextCursor;
          setHasMore(Boolean(res.hasMore));
        }
      } catch {
        /* silent — the picker degrades to whatever is already loaded */
      } finally {
        if (requestId === latestRequest.current) {
          setLoadingMore(false);
          setLoadingInit(false);
          inFlight.current = false;
        }
      }
    },
    [roles],
  );

  useEffect(() => {
    if (enabled) fetchUsers("", null);
  }, [fetchUsers, enabled]);

  const search = useCallback(
    (term) => {
      clearTimeout(debounceRef.current);
      searchRef.current = term;
      debounceRef.current = setTimeout(() => {
        cursorRef.current = null;
        inFlight.current = false;
        fetchUsers(term, null);
      }, 300);
    },
    [fetchUsers],
  );

  const loadMore = useCallback(() => {
    if (inFlight.current || !hasMore || !cursorRef.current) return;
    inFlight.current = true;
    fetchUsers(searchRef.current, cursorRef.current);
  }, [hasMore, fetchUsers]);

  useEffect(() => () => clearTimeout(debounceRef.current), []);

  return { users, loadingInit, loadingMore, hasMore, search, loadMore };
}
